<div class="flex flex-col h-screen" *transloco="let t">
  <!-- Mobile Header -->
  <div
    class="md:hidden flex items-center justify-between p-4 bg-p-content-background border-b border-p-content-border shrink-0 relative z-2001"
    style="height: var(--sb-mobile-header-height)"
  >
    <a [routerLink]="['/']" class="flex items-center" (click)="mobileMenuVisible.set(false)" aria-label="Home">
      @if (isDarkMode()) {
        <img src="/assets/logo/Shine_StoreSync_dark.svg" class="h-10 max-w-full" alt="Shine StoreSync logo" />
      } @else {
        <img src="/assets/logo/Shine_StoreSync_light.svg" class="h-10 max-w-full" alt="Shine StoreSync logo" />
      }
    </a>
    <p-button
      [text]="true"
      severity="secondary"
      (click)="mobileMenuVisible.set(!mobileMenuVisible())"
      [ariaLabel]="mobileMenuVisible() ? t('sidebar.close') : t('sidebar.open')"
      [attr.aria-expanded]="mobileMenuVisible()"
    >
      <fa-icon [icon]="['fal', mobileMenuVisible() ? 'xmark' : 'bars']" size="lg" />
    </p-button>
  </div>

  <!-- Main Content Grid: Sidebar + Viewport -->
  <div
    class="grid grid-cols-1 grow relative overflow-hidden min-h-0"
    [ngClass]="desktopMenuCollapsed() ? 'md:grid-cols-[72px_1fr]' : 'md:grid-cols-[280px_1fr]'"
  >
    <div class="hidden md:block h-screen transition-[width] duration-300 relative group">
      <sb-sidebar-menu
        [activeRouteGroup]="activeRouteGroup()"
        [activeAccountingGroup]="activeAccountingGroup()"
        [collapsed]="desktopMenuCollapsed()"
        (toggleCollapse)="toggleDesktopMenu()"
      />
      <p-button
        rounded
        severity="secondary"
        outlined
        class="flex absolute right-0 top-4 translate-x-1/2 z-20"
        styleClass="bg-p-content-background! hover:bg-p-background-focus-color!"
        (click)="toggleDesktopMenu()"
        [attr.aria-label]="desktopMenuCollapsed() ? t('sidebar.open') : t('sidebar.close')"
      >
        <ng-template #icon>
          <fa-icon [icon]="['fal', desktopMenuCollapsed() ? 'chevron-right' : 'chevron-left']" [fixedWidth]="true" />
        </ng-template>
      </p-button>
    </div>

    <div class="w-full overflow-hidden pt-4 h-full" role="main">
      @if (
        (activeRouteGroup() !== 'accounting' && activeRouteGroup() !== 'crm' && activeRouteGroup() !== 'invoicing') ||
        activeAccountingGroup() === 'order-view'
      ) {
        <div class="h-full px-6 overflow-auto relative" cdkScrollable>
          <router-outlet></router-outlet>
        </div>
      }
      @if (
        (activeRouteGroup() === 'accounting' || activeRouteGroup() === 'crm' || activeRouteGroup() === 'invoicing') &&
        activeAccountingGroup() !== 'order-view'
      ) {
        <div class="h-full px-4 md:px-6 grid grid-rows-[auto{{ getTabMenuItems()?.length ? '_1fr' : '' }}]">
          @if (getTabMenuItems()?.length || getGatewayMenuItems()?.length) {
            <nav class="flex items-center overflow-x-auto no-scrollbar" aria-label="Sub-navigation">
              <p-tabs *sbRecreateOnChange="getTabMenuItems()" [value]="getActiveTabMenuItem()?.routerLink?.[0]">
                <p-tablist>
                  @for (item of getTabMenuItems(); track item.routerLink) {
                    @if (
                      !item.requiresShopify ||
                      hasDefaultStoreShopifySalesChannel() ||
                      (defaultStore() === undefined && hasShopifySalesChannel())
                    ) {
                      <a
                        [sbNavigateIfInactive]="item.routerLink[0]"
                        class="p-tab p-ripple"
                        [class.p-tab-active]="isActiveRoute(item.routerLink[0])"
                      >
                        {{ item.label }}
                      </a>
                    }
                  }
                </p-tablist>
              </p-tabs>

              @if (getGatewayMenuItems()?.length) {
                <div
                  class="border-b-p-content-border border-b h-full flex items-center"
                  [class.border-b-p-color-primary!]="getActiveGatewayMenuItem()"
                >
                  <p-button
                    [severity]="getActiveGatewayMenuItem() ? 'primary' : 'secondary'"
                    [text]="true"
                    (click)="toggleMenu(gatewayMenu, $event)"
                    aria-label="More options"
                  >
                    <fa-icon [icon]="['far', 'ellipsis']" size="lg" [fixedWidth]="true" />
                  </p-button>
                  <p-tieredMenu #gatewayMenu [model]="getGatewayMenuItems()" [popup]="true">
                    <ng-template #item let-item>
                      <a
                        [sbNavigateIfInactive]="item.routerLink[0]"
                        class="flex items-center px-4 py-2 cursor-pointer hover:bg-p-background-focus-color"
                        [class.text-p-color-primary]="isActiveRoute(item.routerLink[0])"
                      >
                        {{ item.label }}
                      </a>
                    </ng-template>
                  </p-tieredMenu>
                </div>
              }
              <div class="border-b-p-content-border border-b grow h-full flex items-center"></div>
            </nav>
          }
          <div class="min-h-0 overflow-hidden relative">
            <router-outlet />
          </div>
        </div>
      }
    </div>
  </div>

  <!-- Mobile Menu Drawer (Fullscreen overlay) -->
  <p-drawer
    [(visible)]="mobileMenuVisible"
    [showCloseIcon]="false"
    [baseZIndex]="2000"
    [modal]="false"
    [style]="{
      height: 'calc(100vh - var(--sb-mobile-header-height))',
      top: 'var(--sb-mobile-header-height)',
      width: '100%',
    }"
  >
    <sb-sidebar-menu
      [activeRouteGroup]="activeRouteGroup()"
      [activeAccountingGroup]="activeAccountingGroup()"
      [hideLogo]="true"
      (actionCompleted)="mobileMenuVisible.set(false)"
    />
  </p-drawer>

  <!-- Global Slide-in Drawer -->
  <p-drawer
    [(visible)]="sidebarVisible"
    [position]="'right'"
    [style]="slideInDrawerStyle()"
    [baseZIndex]="1000"
    [showCloseIcon]="false"
    [modal]="slideInBackdrop()"
    (onHide)="onCloseSlideIn()"
  >
    @if (slideInPortal()) {
      <ng-template [cdkPortalOutlet]="slideInPortal()" />
    }
  </p-drawer>
</div>

<sb-dev-tools />
